import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { trpc } from "@/lib/trpc";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Entrada {
  id: number;
  numeroAgendamento: string;
  pedidoId?: number | null;
  nome: string;
  status: string;
  dataAgendamento: string | Date;
  emitenteId: number;
  emitenteNome?: string;
  volumes?: number;
  conferentes?: string;
  notaFiscal?: string;
  observacoes?: string;
  itens?: EntradaItem[];
  tipo?: string;
  atualizadoEm?: string | Date;
}

interface EntradaItem {
  id: number;
  produtoCod: string;
  produtoDescricao: string;
  codigoEntrada: string;
  quantidade: number;
  custoUnitario: number;
  custoTotal: number;
  retrabalhar: boolean;
}

type AcaoMassa = "receber" | "iniciar_conferencia" | "finalizar_conferencia" | "finalizar_entrada" | "finalizar_entrada_nao_fiscal";

const UPSELLER_STATUS_LABELS: Record<string, string> = {
  pendente: "Pendente",
  processando: "Processando",
  concluido: "Concluído",
  autenticacao_necessaria: "Autenticação necessária",
  erro: "Erro",
  nao_solicitado: "Não solicitado",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  agendado:       { label: "Agendado",        color: "bg-blue-100 text-blue-800" },
  recebido:       { label: "Recebido",        color: "bg-yellow-100 text-yellow-800" },
  em_conferencia: { label: "Em Conferência",  color: "bg-purple-100 text-purple-800" },
  conferido:      { label: "Conferido",       color: "bg-green-100 text-green-800" },
  finalizado:     { label: "Finalizado",      color: "bg-teal-100 text-teal-800" },
  enderecado:     { label: "Endereçado",      color: "bg-gray-100 text-gray-800" },
};

const ACOES_MASSA: Record<AcaoMassa, { label: string; status: string }> = {
  receber: { label: "Receber", status: "agendado" },
  iniciar_conferencia: { label: "Iniciar conferência", status: "recebido" },
  finalizar_conferencia: { label: "Finalizar conferência", status: "em_conferencia" },
  finalizar_entrada: { label: "Finalizar entrada", status: "conferido" },
  finalizar_entrada_nao_fiscal: { label: "Finalizar entrada não Fiscal", status: "conferido" },
};

function formatDate(val: string | Date | undefined): string {
  if (!val) return "-";
  if (val instanceof Date) return val.toISOString().split("T")[0];
  return String(val).split("T")[0];
}

function getFlagDataAgendamento(dataAgendamento: string | Date | undefined, status: string) {
  if (status !== "agendado" || !dataAgendamento) return null;
  const data = formatDate(dataAgendamento);
  const hoje = new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
  if (data < hoje) return { label: "Atrasado", color: "bg-red-100 text-red-800" };
  if (data === hoje) return { label: "Hoje", color: "bg-yellow-100 text-yellow-800" };
  return null;
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_LABELS[status] ?? { label: status, color: "bg-gray-100 text-gray-700" };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.color}`}>
      {s.label}
    </span>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Recebimentos() {
  const { user } = useGoogleAuth();
  const isCliente = user?.role === "cliente";
  const isAdmin = user?.role === "admin";

  // Filtros
  const [search, setSearch] = useState("");
  const [filterEmitenteId, setFilterEmitenteId] = useState<string>("todos");
  const [filterStatus, setFilterStatus] = useState<string>("todos");
  const [filterDataAgendamento, setFilterDataAgendamento] = useState<"todos" | "atrasado" | "hoje">("todos");
  const [filterDevolucaoMecom, setFilterDevolucaoMecom] = useState(false);
  const [filterNaoDevolucaoMecom, setFilterNaoDevolucaoMecom] = useState(false);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 500;
  const [entradasSelecionadas, setEntradasSelecionadas] = useState<number[]>([]);
  const [acaoMassa, setAcaoMassa] = useState<AcaoMassa | null>(null);
  const [volumesMassa, setVolumesMassa] = useState<Record<string, number>>({});
  const [conferentesMassa, setConferentesMassa] = useState<Array<{ id: number; nome: string }>>([]);
  const [operacoesMassa, setOperacoesMassa] = useState<Record<string, number>>({});
  const [solicitarUpsellerMassa, setSolicitarUpsellerMassa] = useState(true);
  const [resultadoMassa, setResultadoMassa] = useState<Array<{ id: number; numeroAgendamento: string; resultado: "concluido" | "ignorado" | "erro"; mensagem: string }> | null>(null);
  const [modeloPlanilhaModal, setModeloPlanilhaModal] = useState(false);
  const [textoPlanilhaModal, setTextoPlanilhaModal] = useState<{ agendamentos: string; nomes: string } | null>(null);

  // Listagem
  const { data: entradasData, refetch, isLoading } = trpc.entradas.list.useQuery(
    {
      emitenteId: filterEmitenteId === "todos" ? undefined : Number(filterEmitenteId),
      status: filterStatus === "todos" ? undefined : filterStatus,
      filtroDataAgendamento: filterDataAgendamento !== "todos" ? filterDataAgendamento : undefined,
      search: search.trim() || undefined,
      devolucaoMecom: filterDevolucaoMecom || undefined,
      naoDevolucaoMecom: filterNaoDevolucaoMecom || undefined,
      dataInicio: dataInicio || undefined,
      dataFim: dataFim || undefined,
      page,
      pageSize: PAGE_SIZE,
    },
    { enabled: !!user }
  );
  const allEntradas: Entrada[] = useMemo(() => (entradasData as any)?.data ?? [], [entradasData]);
  const totalEntradas = (entradasData as any)?.total ?? 0;
  const totalPages = Math.max(1, (entradasData as any)?.totalPages ?? 1);
  const paginatedEntradas = allEntradas;
  const entradaIdsUpseller = useMemo(() => allEntradas.map((entrada) => entrada.id), [allEntradas]);
  const { data: jobsUpseller = [], refetch: refetchJobsUpseller } = trpc.upsellerEntrada.listarJobs.useQuery(
    { entradaIds: entradaIdsUpseller },
    { enabled: entradaIdsUpseller.length > 0 }
  );
  const jobsUpsellerPorEntrada = new Map((jobsUpseller as any[]).map((job) => [Number(job.entradaId), job]));
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, { enabled: !!user });
  const { data: relatorioData = [] } = trpc.entradas.relatorio.useQuery(
    {
      entradaIds: entradasSelecionadas.length > 0 ? entradasSelecionadas : undefined,
      emitenteId: entradasSelecionadas.length > 0 || filterEmitenteId === "todos" ? undefined : Number(filterEmitenteId),
      status: entradasSelecionadas.length > 0 || filterStatus === "todos" ? undefined : filterStatus,
      devolucaoMecom: entradasSelecionadas.length > 0 ? undefined : filterDevolucaoMecom || undefined,
      naoDevolucaoMecom: entradasSelecionadas.length > 0 ? undefined : filterNaoDevolucaoMecom || undefined,
      dataInicio: entradasSelecionadas.length > 0 ? undefined : dataInicio || undefined,
      dataFim: entradasSelecionadas.length > 0 ? undefined : dataFim || undefined,
      search: entradasSelecionadas.length > 0 ? undefined : search.trim() || undefined,
    },
    { enabled: !!user }
  );
  const entradasSelecionadasDados = allEntradas.filter((entrada) => entradasSelecionadas.includes(entrada.id));
  const entradasDaAcao = acaoMassa ? entradasSelecionadasDados.filter((entrada) => entrada.status === ACOES_MASSA[acaoMassa].status) : [];
  const entradasIncompativeis = entradasSelecionadasDados.length - entradasDaAcao.length;
  const entradaIdsComCustoZero = resultadoMassa
    ? Array.from(new Set(resultadoMassa
        .filter((resultado) => resultado.resultado === "erro" && resultado.mensagem.startsWith("Entrada normal não pode ser finalizada com custo zero"))
        .map((resultado) => resultado.id)))
    : [];

  // ── Modais ─────────────────────────────────────────────────────────────────

  // Modal: Visualizar
  const [viewEntrada, setViewEntrada] = useState<Entrada | null>(null);

  // Modal: Receber
  const [receberEntrada, setReceberEntrada] = useState<Entrada | null>(null);
  const [volumes, setVolumes] = useState<number>(1);

  // Modal: Conferir
  const [conferirEntrada, setConferirEntrada] = useState<Entrada | null>(null);
  const [selectedConferentes, setSelectedConferentes] = useState<Array<{ id: number; nome: string }>>([]);

  // Modal: Finalizar Conferência
  const [finalizarConfEntrada, setFinalizarConfEntrada] = useState<Entrada | null>(null);

  // Modal: Finalizar Entrada
  const [finalizarEntradaModal, setFinalizarEntradaModal] = useState<Entrada | null>(null);
  const [selectedOperacaoId, setSelectedOperacaoId] = useState<number | null>(null);
  const [solicitarUpsellerFiscal, setSolicitarUpsellerFiscal] = useState(true);

  // Modal: Finalizar Entrada não Fiscal
  const [finalizarEntradaNaoFiscalModal, setFinalizarEntradaNaoFiscalModal] = useState<Entrada | null>(null);
  const [solicitarUpsellerNaoFiscal, setSolicitarUpsellerNaoFiscal] = useState(true);

  // Modal: Endereçar
  const [enderecaModal, setEnderecaModal] = useState<Entrada | null>(null);
  const [itensEnderecos, setItensEnderecos] = useState<Record<number, Array<{ endereco: string; quantidade: number }>>>({});
  const [itensSelecionadosEnderecamento, setItensSelecionadosEnderecamento] = useState<number[]>([]);
  const [enderecoMassa, setEnderecoMassa] = useState("");

  // Modal: Retificar Volumes
  const [retificarModal, setRetificarModal] = useState<Entrada | null>(null);
  const [retificarVolumes, setRetificarVolumes] = useState<number>(1);

  // Modal: Selecionar Endereço (para o endereçamento)
  const [enderecoSelectModal, setEnderecoSelectModal] = useState<{ itemId: number; idx: number } | null>(null);
  const [enderecoSearch, setEnderecoSearch] = useState("");

  // ── Queries auxiliares ─────────────────────────────────────────────────────

  const { data: usersData = [] } = trpc.pedidos.listUsuarios.useQuery();
  const { data: operacoesData = [] } = trpc.operationNatures.list.useQuery(
    undefined,
    { enabled: !!finalizarEntradaModal || acaoMassa === "finalizar_entrada" }
  );
  const operacoesRemessa = (operacoesData as any[]).filter((o: any) => {
    const tipoOk = o.tipo === "remessa_armazem" || o.tipoOperacao === "remessa_armazem";
    const emitenteOk = !o.emitenteId || o.emitenteId === finalizarEntradaModal?.emitenteId;
    return tipoOk && emitenteOk;
  });
  const operacoesRemessaMassa = (operacoesData as any[]).filter((o: any) => o.tipo === "remessa_armazem" || o.tipoOperacao === "remessa_armazem");

  const { data: enderecosData = [] } = trpc.addresses.list.useQuery(
    undefined,
    { enabled: !!enderecaModal }
  );

  const { data: entradaDetalhes } = trpc.entradas.getById.useQuery(
    { id: viewEntrada?.id ?? 0 },
    { enabled: !!viewEntrada }
  );

  const { data: entradaConferir, isLoading: isLoadingConferir } = trpc.entradas.getById.useQuery(
    { id: conferirEntrada?.id ?? 0 },
    { enabled: !!conferirEntrada }
  );

  const { data: entradaEnderecar } = trpc.entradas.getById.useQuery(
    { id: enderecaModal?.id ?? 0 },
    { enabled: !!enderecaModal }
  );
  const itensDoEnderecamento = ((entradaEnderecar as any)?.itens ?? []) as any[];
  const todosItensEnderecamentoSelecionados = itensDoEnderecamento.length > 0 && itensDoEnderecamento.every((item: any) => itensSelecionadosEnderecamento.includes(item.id));

  // ── Mutations ──────────────────────────────────────────────────────────────

  const receberMutation = trpc.entradas.receber.useMutation({
    onSuccess: () => { toast.success("Entrada recebida com sucesso!"); setReceberEntrada(null); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const iniciarConfMutation = trpc.entradas.iniciarConferencia.useMutation({
    onSuccess: () => { toast.success("Conferência iniciada!"); setConferirEntrada(null); setSelectedConferentes([]); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const finalizarConfMutation = trpc.entradas.finalizarConferencia.useMutation({
    onSuccess: () => { toast.success("Conferência finalizada!"); setFinalizarConfEntrada(null); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const finalizarEntradaMutation = trpc.upsellerEntrada.finalizarFiscal.useMutation({
    onSuccess: (resultado) => { console.info("[UPSELLER_FLUXO]", { etapa: "ui-finalizar-fiscal-sucesso", resultado }); toast.success("Entrada finalizada e itens lançados no estoque!"); setFinalizarEntradaModal(null); setSelectedOperacaoId(null); refetch(); },
    onError: (e) => { console.error("[UPSELLER_FLUXO]", { etapa: "ui-finalizar-fiscal-erro", erro: e.message }); toast.error(e.message); },
  });

  const finalizarEntradaNaoFiscalMutation = trpc.upsellerEntrada.finalizarNaoFiscal.useMutation({
    onSuccess: (resultado) => { console.info("[UPSELLER_FLUXO]", { etapa: "ui-finalizar-nao-fiscal-sucesso", resultado }); toast.success("Entrada não fiscal finalizada e itens lançados no estoque!"); setFinalizarEntradaNaoFiscalModal(null); refetch(); },
    onError: (e) => { console.error("[UPSELLER_FLUXO]", { etapa: "ui-finalizar-nao-fiscal-erro", erro: e.message }); toast.error(e.message); },
  });

  const finalizarMassaUpsellerMutation = trpc.upsellerEntrada.finalizarMassa.useMutation({
    onSuccess: (resultados) => {
      console.info("[UPSELLER_FLUXO]", { etapa: "ui-finalizar-massa-sucesso", resultados });
      setResultadoMassa(resultados as any);
      setAcaoMassa(null);
      refetch();
    },
    onError: (e) => { console.error("[UPSELLER_FLUXO]", { etapa: "ui-finalizar-massa-erro", erro: e.message }); toast.error(e.message); },
  });

  const reenviarJobUpsellerMutation = trpc.upsellerEntrada.reenviarErro.useMutation({
    onSuccess: () => { toast.success("Tarefa UpSeller reenviada."); refetchJobsUpseller(); },
    onError: (e) => toast.error(e.message),
  });

  const enderecaMutation = trpc.entradas.enderecaItens.useMutation({
    onSuccess: () => { toast.success("Itens endereçados com sucesso!"); setEnderecaModal(null); setItensEnderecos({}); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const retificarMutation = trpc.entradas.receber.useMutation({
    onSuccess: () => { toast.success("Volumes retificados!"); setRetificarModal(null); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.entradas.delete.useMutation({
    onSuccess: () => { toast.success("Entrada excluída!"); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const acoesMassaMutation = trpc.entradas.acoesMassa.useMutation({
    onSuccess: (resultados) => {
      setResultadoMassa(resultados as any);
      setAcaoMassa(null);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const corrigirCustosZeradosMutation = trpc.entradas.corrigirCustosZerados.useMutation({
    onSuccess: (resultado) => {
      toast.success(`${resultado.itensAtualizados} custo(s) zerado(s) ajustado(s) para 0,01.`);
      setResultadoMassa((atual) => atual?.map((item) => entradaIdsComCustoZero.includes(item.id)
        ? { ...item, resultado: "concluido" as const, mensagem: "Custos zerados ajustados para 0,01." }
        : item) ?? null);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleReceber() {
    if (!receberEntrada || volumes < 1) return;
    receberMutation.mutate({ id: receberEntrada.id, volumes });
  }

  function toggleConferente(u: { id: number; nome: string }) {
    setSelectedConferentes(prev =>
      prev.find(c => c.id === u.id)
        ? prev.filter(c => c.id !== u.id)
        : [...prev, { id: u.id, nome: u.nome }]
    );
  }

  function handleIniciarConferencia() {
    if (!conferirEntrada || selectedConferentes.length === 0) {
      toast.error("Selecione ao menos um conferente");
      return;
    }
    iniciarConfMutation.mutate({ id: conferirEntrada.id, conferentes: selectedConferentes });
  }

  function handleFinalizarConferencia() {
    if (!finalizarConfEntrada) return;
    finalizarConfMutation.mutate({ id: finalizarConfEntrada.id });
  }

  function handleFinalizarEntrada() {
    if (!finalizarEntradaModal) return;
    const isCancelamento = finalizarEntradaModal.tipo === 'cancelamento';
    if (!isCancelamento && !selectedOperacaoId) {
      toast.error("Selecione uma natureza de operação");
      return;
    }
    console.info("[UPSELLER_FLUXO]", { etapa: "ui-clique-finalizar-fiscal", entradaId: finalizarEntradaModal.id, operacaoNaturezaId: isCancelamento ? undefined : selectedOperacaoId, solicitarUpseller: solicitarUpsellerFiscal });
    finalizarEntradaMutation.mutate({
      id: finalizarEntradaModal.id,
      operacaoNaturezaId: isCancelamento ? undefined : selectedOperacaoId!,
      solicitarUpseller: solicitarUpsellerFiscal,
    });
  }

  function handleFinalizarEntradaNaoFiscal() {
    if (!finalizarEntradaNaoFiscalModal) return;
    console.info("[UPSELLER_FLUXO]", { etapa: "ui-clique-finalizar-nao-fiscal", entradaId: finalizarEntradaNaoFiscalModal.id, solicitarUpseller: solicitarUpsellerNaoFiscal });
    finalizarEntradaNaoFiscalMutation.mutate({ id: finalizarEntradaNaoFiscalModal.id, solicitarUpseller: solicitarUpsellerNaoFiscal });
  }

  function handleEnderecar() {
    if (!enderecaModal || !entradaEnderecar) return;
    const itens = (entradaEnderecar as any).itens ?? [];
    // Validar que a soma das quantidades é IGUAL à quantidade total do item (todas as unidades devem ser alocadas)
    for (const item of itens) {
      const endList = itensEnderecos[item.id] ?? [];
      const somaQtd = endList.reduce((acc: number, e: { endereco: string; quantidade: number }) => acc + (e.quantidade || 0), 0);
      if (somaQtd > Number(item.quantidade)) {
        toast.error(`Item ${item.codigoEntrada || item.produtoCod}: soma das quantidades (${somaQtd}) excede o total do item (${parseInt(item.quantidade)})`);
        return;
      }
      if (somaQtd < Number(item.quantidade)) {
        toast.error(`Item ${item.codigoEntrada || item.produtoCod}: soma das quantidades alocadas (${somaQtd}) é menor que o total do item (${parseInt(item.quantidade)}). Todas as unidades devem ser endereçadas.`);
        return;
      }
    }
    const payload = itens
      .map((item: any) => ({
        itemId: item.id,
        produtoCod: item.codigoEntrada || item.produtoCod,
        enderecos: (itensEnderecos[item.id] ?? []).filter((e: { endereco: string; quantidade: number }) => e.endereco && e.quantidade > 0),
      }))
      .filter((i: any) => i.enderecos.length > 0);

    if (payload.length === 0) {
      toast.error("Informe ao menos um endereço com quantidade para ao menos um item");
      return;
    }
    enderecaMutation.mutate({ id: enderecaModal.id, itensEnderecos: payload });
  }

  function toggleItemEnderecamento(itemId: number) {
    setItensSelecionadosEnderecamento((atual) => atual.includes(itemId) ? atual.filter((id) => id !== itemId) : [...atual, itemId]);
  }

  function toggleTodosItensEnderecamento() {
    setItensSelecionadosEnderecamento(todosItensEnderecamentoSelecionados ? [] : itensDoEnderecamento.map((item: any) => item.id));
  }

  function aplicarEnderecoMassa() {
    if (itensSelecionadosEnderecamento.length === 0) {
      toast.error("Selecione ao menos um item");
      return;
    }
    if (!enderecoMassa) {
      toast.error("Selecione um endereço para os itens marcados");
      return;
    }
    setItensEnderecos((atual) => {
      const proximo = { ...atual };
      itensDoEnderecamento
        .filter((item: any) => itensSelecionadosEnderecamento.includes(item.id))
        .forEach((item: any) => {
          proximo[item.id] = [{ endereco: enderecoMassa, quantidade: Number(item.quantidade) }];
        });
      return proximo;
    });
  }

  function handleRetificar() {
    if (!retificarModal || retificarVolumes < 1) return;
    retificarMutation.mutate({ id: retificarModal.id, volumes: retificarVolumes });
  }

  function toggleEntradaSelecionada(id: number) {
    setEntradasSelecionadas((atual) => atual.includes(id) ? atual.filter((itemId) => itemId !== id) : [...atual, id]);
  }

  function togglePaginaSelecionada() {
    const idsPagina = paginatedEntradas.map((entrada) => entrada.id);
    const todosSelecionados = idsPagina.length > 0 && idsPagina.every((id) => entradasSelecionadas.includes(id));
    setEntradasSelecionadas((atual) => todosSelecionados ? atual.filter((id) => !idsPagina.includes(id)) : [...new Set([...atual, ...idsPagina])]);
  }

  function abrirAcaoMassa() {
    if (entradasSelecionadasDados.length === 0) return;
    setAcaoMassa("receber");
    setVolumesMassa(Object.fromEntries(entradasSelecionadasDados.filter((entrada) => entrada.status === "agendado").map((entrada) => [String(entrada.id), 1])));
    setConferentesMassa([]);
    setOperacoesMassa({});
    setSolicitarUpsellerMassa(true);
  }

  function toggleConferenteMassa(usuario: { id: number; nome: string }) {
    setConferentesMassa((atual) => atual.find((conferente) => conferente.id === usuario.id)
      ? atual.filter((conferente) => conferente.id !== usuario.id)
      : [...atual, usuario]);
  }

  function executarAcaoMassa() {
    if (!acaoMassa || entradasDaAcao.length === 0) return;
    if (acaoMassa === "iniciar_conferencia" && conferentesMassa.length === 0) {
      toast.error("Selecione ao menos um conferente");
      return;
    }
    if (acaoMassa === "finalizar_entrada") {
      const emitentesPendentes = entradasDaAcao.filter((entrada) => entrada.tipo !== "cancelamento" && !operacoesMassa[String(entrada.emitenteId)]);
      if (emitentesPendentes.length > 0) {
        toast.error("Selecione a natureza de operação de cada emitente");
        return;
      }
    }
    const dadosAcaoMassa = {
      entradaIds: entradasDaAcao.map((entrada) => entrada.id),
      acao: acaoMassa,
      volumesPorEntrada: acaoMassa === "receber" ? volumesMassa : undefined,
      conferentes: acaoMassa === "iniciar_conferencia" ? conferentesMassa : undefined,
      operacaoNaturezaPorEmitente: acaoMassa === "finalizar_entrada" ? operacoesMassa : undefined,
    };
    if (acaoMassa === "finalizar_entrada" || acaoMassa === "finalizar_entrada_nao_fiscal") {
      console.info("[UPSELLER_FLUXO]", { etapa: "ui-clique-finalizar-massa", entradaIds: dadosAcaoMassa.entradaIds, acao: dadosAcaoMassa.acao, operacaoNaturezaPorEmitente: dadosAcaoMassa.operacaoNaturezaPorEmitente, solicitarUpseller: solicitarUpsellerMassa });
      finalizarMassaUpsellerMutation.mutate({
        entradaIds: dadosAcaoMassa.entradaIds,
        acao: dadosAcaoMassa.acao as "finalizar_entrada" | "finalizar_entrada_nao_fiscal",
        operacaoNaturezaPorEmitente: dadosAcaoMassa.operacaoNaturezaPorEmitente,
        solicitarUpseller: solicitarUpsellerMassa,
      });
      return;
    }
    acoesMassaMutation.mutate(dadosAcaoMassa);
  }

  function handleBaixarPlanilha(modelo: "padrao" | "upseller") {
    const dadosDaPlanilha = entradasSelecionadas.length > 0
      ? (relatorioData as any[]).filter((item) => entradasSelecionadas.includes(item.id))
      : (relatorioData as any[]);
    if (dadosDaPlanilha.length === 0) {
      toast.error(entradasSelecionadas.length > 0 ? "Nenhum item encontrado para os agendamentos selecionados" : "Nenhum item encontrado para os filtros atuais");
      return;
    }
    const entradasDaPlanilha = Array.from(new Map(dadosDaPlanilha.map((item) => [item.id, item])).values());
    const numerosAgendamento = entradasDaPlanilha.map((item) => item.numeroAgendamento).filter(Boolean).join(", ");
    const nomes = entradasDaPlanilha.map((item) => item.nome).filter(Boolean).join(", ");
    const itensPorCodigo = new Map<string, any>();
    dadosDaPlanilha.forEach((item, indice) => {
      const codigoEntrada = String(item.codigoEntrada ?? "").trim().toUpperCase();
      const chave = codigoEntrada || `__sem_codigo_${indice}`;
      const existente = itensPorCodigo.get(chave);
      if (existente) {
        existente.quantidade = Number(existente.quantidade || 0) + Number(item.quantidade || 0);
      } else {
        itensPorCodigo.set(chave, { ...item, codigoEntrada, quantidade: Number(item.quantidade || 0) });
      }
    });
    const itensAgrupados = Array.from(itensPorCodigo.values());
    let linhas: any[];
    if (modelo === "upseller") {
      linhas = itensAgrupados.map((item) => ({
        "SKU*": item.codigoEntrada,
        "Quantidade*\n(limite 1-999999)": item.quantidade,
        "N° do Estante\nApenas estantes existentes, serão filtrados se o estante selecionado estiver cheio ou ficará cheio após a importação": "",
        "Custo a Unidade\nSe não preencher, preço de compra será definido como custo de unidade\n": item.custoUnitario !== null && item.custoUnitario !== undefined && item.custoUnitario !== "" && Number(item.custoUnitario) === 0 ? 0.01 : (item.custoUnitario ?? ""),
      }));
    } else {
      linhas = itensAgrupados.map((item) => {
        let conferentes = "";
        try { conferentes = (JSON.parse(item.conferentes || "[]") as Array<{ nome: string }>).map((conferente) => conferente.nome).join(", "); } catch { conferentes = item.conferentes || ""; }
        return {
          "Nº Agendamento": item.numeroAgendamento,
          Nome: item.nome,
          Emitente: item.emitenteNome || "",
          Status: STATUS_LABELS[item.status]?.label ?? item.status,
          "Data Agendamento": formatDate(item.dataAgendamento),
          Volumes: item.volumes ?? "",
          "Nota Fiscal": item.notaFiscal ?? "",
          Observações: item.observacoes ?? "",
          Conferentes: conferentes,
          "Cód Produto": item.produtoCod ?? "",
          "Cód Entrada": item.codigoEntrada ?? "",
          "Descrição Produto": item.produtoDescricao ?? "",
          Retrabalhar: item.retrabalhar ? "Sim" : "Não",
          Quantidade: item.quantidade ?? "",
          Unidade: item.unidade ?? "",
          "Custo Unitário": item.custoUnitario ?? "",
          "Custo Total": item.custoTotal ?? "",
          "Origem Fiscal": item.origemFiscal ?? "",
          "Criado Em": item.criadoEm ? new Date(item.criadoEm).toLocaleString("pt-BR") : "",
        };
      });
    }
    const planilha = XLSX.utils.json_to_sheet(linhas);
    const arquivo = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(arquivo, planilha, modelo === "upseller" ? "Sheet1" : "Entradas");
    XLSX.writeFile(arquivo, `${modelo === "upseller" ? "upseller" : "entradas_por_item"}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setModeloPlanilhaModal(false);
    setTextoPlanilhaModal({ agendamentos: numerosAgendamento, nomes });
  }

  function handleExportarDetalhe() {
    if (!entradaDetalhes) return;
    const entrada = entradaDetalhes as any;
    const itens = entrada.itens?.length > 0 ? entrada.itens : [{}];
    let conferentes = "";
    try { conferentes = (JSON.parse(entrada.conferentes || "[]") as Array<{ nome: string }>).map((conferente) => conferente.nome).join(", "); } catch { conferentes = entrada.conferentes || ""; }
    const linhas = itens.map((item: any) => ({
      "Nº Agendamento": entrada.numeroAgendamento,
      Nome: entrada.nome,
      Emitente: entrada.emitenteNome || "",
      Status: STATUS_LABELS[entrada.status]?.label ?? entrada.status,
      "Data Agendamento": formatDate(entrada.dataAgendamento),
      Volumes: entrada.volumes ?? "",
      "Nota Fiscal": entrada.notaFiscal ?? "",
      Observações: entrada.observacoes ?? "",
      Conferentes: conferentes,
      "Cód Produto": item.produtoCod ?? "",
      "Cód Entrada": item.codigoEntrada ?? "",
      "Descrição Produto": item.produtoDescricao ?? "",
      Retrabalhar: item.retrabalhar ? "Sim" : "Não",
      Quantidade: item.quantidade ?? "",
      Unidade: item.unidade ?? "",
      "Custo Unitário": item.custoUnitario ?? "",
      "Custo Total": item.custoTotal ?? "",
      "Origem Fiscal": item.origemFiscal ?? "",
      "Criado Em": entrada.criadoEm ? new Date(entrada.criadoEm).toLocaleString("pt-BR") : "",
    }));
    const planilha = XLSX.utils.json_to_sheet(linhas);
    const arquivo = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(arquivo, planilha, "Entradas");
    XLSX.writeFile(arquivo, `entrada_${entrada.numeroAgendamento}.xlsx`);
  }

  function handleExcluirEntrada(entrada: Entrada) {
    if (!window.confirm(`Excluir a entrada ${entrada.numeroAgendamento}?`)) return;
    deleteMutation.mutate({ id: entrada.id });
  }

  function handleImprimirVia(entrada: Entrada) {
    const conferentes = entrada.conferentes
      ? (() => { try { return JSON.parse(entrada.conferentes); } catch { return []; } })()
      : [];

    const itens = (entrada as any).itens ?? [];
    const html = `
      <html><head><title>Via de Conferência</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
        h2 { margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; }
        .info { margin-bottom: 8px; }
      </style></head>
      <body>
        <h2>Via de Conferência — ${entrada.numeroAgendamento}</h2>
        <div class="info"><strong>Nome:</strong> ${entrada.nome}</div>
        <div class="info"><strong>Emitente:</strong> ${entrada.emitenteNome ?? entrada.emitenteId}</div>
        <div class="info"><strong>Data:</strong> ${formatDate(entrada.dataAgendamento)}</div>
        <div class="info"><strong>Volumes:</strong> ${entrada.volumes ?? "-"}</div>
        <div class="info"><strong>Conferentes:</strong> ${conferentes.map((c: any) => c.nome).join(", ") || "-"}</div>
        <table>
          <thead><tr><th>Cód Produto</th><th>Cód Entrada</th><th>Descrição</th><th>Qtd</th><th>Conferido</th></tr></thead>
          <tbody>
            ${itens.map((i: any) => `<tr>
              <td>${i.produtoCod}</td>
              <td>${i.codigoEntrada}</td>
              <td>${i.produtoDescricao}</td>
              <td>${parseInt(i.quantidade)}</td>
              <td style="width:60px"></td>
            </tr>`).join("")}
          </tbody>
        </table>
      </body></html>
    `;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); w.print(); }
  }


  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Recebimentos</h1>
          <p className="text-sm text-muted-foreground">{totalEntradas} agendamento(s) encontrado(s)</p>
        </div>
        <div className="flex gap-2">
          {entradasSelecionadas.length > 0 && <Button variant="outline" onClick={abrirAcaoMassa}>Ação em massa ({entradasSelecionadas.length})</Button>}
          <Button variant="outline" onClick={() => setModeloPlanilhaModal(true)}>Baixar planilha</Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <Input
            placeholder="Buscar por nome, nº agendamento, emitente, nota fiscal ou SKU..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="flex-1 min-w-[200px]"
          />
          <select
            value={filterEmitenteId}
            onChange={(e) => { setFilterEmitenteId(e.target.value); setPage(1); }}
            className="flex h-10 min-w-[240px] rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="todos">Emitente: Todos</option>
            {(emitentes as any[]).map((emitente) => (
              <option key={emitente.id} value={String(emitente.id)}>{emitente.razaoSocial}</option>
            ))}
          </select>
          <Input type="date" value={dataInicio} onChange={e => { setDataInicio(e.target.value); setPage(1); }} className="w-40" placeholder="Data início" />
          <Input type="date" value={dataFim} onChange={e => { setDataFim(e.target.value); setPage(1); }} className="w-40" placeholder="Data fim" />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setFilterDevolucaoMecom((atual) => !atual); setFilterNaoDevolucaoMecom(false); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterDevolucaoMecom ? "bg-orange-500 text-white border-orange-500" : "bg-white text-gray-600 border-gray-300 hover:border-orange-400"}`}
          >
            Devolução MECOM
          </button>
          <button
            onClick={() => { setFilterNaoDevolucaoMecom((atual) => !atual); setFilterDevolucaoMecom(false); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterNaoDevolucaoMecom ? "bg-orange-500 text-white border-orange-500" : "bg-white text-gray-600 border-gray-300 hover:border-orange-400"}`}
          >
            Não devolução MECOM
          </button>
          {["todos", "agendado", "recebido", "em_conferencia", "conferido", "finalizado", "enderecado"].map(s => (
            <button
              key={s}
              onClick={() => { setFilterStatus(s); setFilterDataAgendamento("todos"); setPage(1); }}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterStatus === s ? "bg-orange-500 text-white border-orange-500" : "bg-white text-gray-600 border-gray-300 hover:border-orange-400"}`}
            >
              {s === "todos" ? "Todos" : STATUS_LABELS[s]?.label ?? s}
            </button>
          ))}
          {([{ value: "atrasado", label: "Atrasados" }, { value: "hoje", label: "Hoje" }] as const).map((filtro) => (
            <button
              key={filtro.value}
              onClick={() => { setFilterDataAgendamento(filtro.value); setFilterStatus("agendado"); setPage(1); }}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filterDataAgendamento === filtro.value ? "bg-orange-500 text-white border-orange-500" : "bg-white text-gray-600 border-gray-300 hover:border-orange-400"}`}
            >
              {filtro.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 w-10"><input type="checkbox" aria-label="Selecionar página" checked={paginatedEntradas.length > 0 && paginatedEntradas.every((entrada) => entradasSelecionadas.includes(entrada.id))} onChange={togglePaginaSelecionada} /></th>
              <th className="text-left px-4 py-3 font-medium">Nº Agendamento</th>
              <th className="text-left px-4 py-3 font-medium">Pedido</th>
              <th className="text-left px-4 py-3 font-medium">Nome</th>
              <th className="text-left px-4 py-3 font-medium">Emitente</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Data</th>
              <th className="text-left px-4 py-3 font-medium">Última Atualização</th>
              <th className="text-left px-4 py-3 font-medium">Volumes</th>
              <th className="text-left px-4 py-3 font-medium">UpSeller</th>
              <th className="text-left px-4 py-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={11} className="text-center py-8 text-muted-foreground">Carregando...</td></tr>
            ) : paginatedEntradas.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-8 text-muted-foreground">Nenhum agendamento encontrado</td></tr>
            ) : paginatedEntradas.map(entrada => (
              <tr key={entrada.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3"><input type="checkbox" aria-label={`Selecionar ${entrada.numeroAgendamento}`} checked={entradasSelecionadas.includes(entrada.id)} onChange={() => toggleEntradaSelecionada(entrada.id)} /></td>
                <td className="px-4 py-3 font-mono text-xs">{entrada.numeroAgendamento}</td>
                <td className="px-4 py-3 font-mono text-xs">#{entrada.id}</td>
                <td className="px-4 py-3">{entrada.nome}</td>
                <td className="px-4 py-3 text-muted-foreground">{entrada.emitenteNome ?? entrada.emitenteId}</td>
                <td className="px-4 py-3"><StatusBadge status={entrada.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>{formatDate(entrada.dataAgendamento)}</span>
                    {(() => {
                      const flag = getFlagDataAgendamento(entrada.dataAgendamento, entrada.status);
                      return flag ? <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${flag.color}`}>{flag.label}</span> : null;
                    })()}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{entrada.atualizadoEm ? new Date(entrada.atualizadoEm).toLocaleString("pt-BR") : "-"}</td>
                <td className="px-4 py-3">{entrada.volumes ?? "-"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {jobsUpsellerPorEntrada.get(entrada.id) ? (
                    <div className="max-w-56">
                      <div>{UPSELLER_STATUS_LABELS[jobsUpsellerPorEntrada.get(entrada.id)?.status] ?? jobsUpsellerPorEntrada.get(entrada.id)?.status}</div>
                      {jobsUpsellerPorEntrada.get(entrada.id)?.status === "erro" && jobsUpsellerPorEntrada.get(entrada.id)?.erro && (
                        <>
                          <div className="mt-1 text-red-600">{jobsUpsellerPorEntrada.get(entrada.id)?.erro}</div>
                          <button
                            type="button"
                            disabled={reenviarJobUpsellerMutation.isPending}
                            onClick={() => reenviarJobUpsellerMutation.mutate({ entradaId: entrada.id })}
                            className="mt-1 rounded border border-orange-300 px-2 py-1 text-xs font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                          >
                            {reenviarJobUpsellerMutation.isPending ? "Reenviando..." : "Reenviar"}
                          </button>
                        </>
                      )}
                    </div>
                  ) : "-"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {/* Botões fixos */}
                    <button
                      onClick={() => setViewEntrada(entrada)}
                      title="Visualizar"
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    </button>
                    {isAdmin && ["agendado", "recebido", "em_conferencia", "conferido"].includes(entrada.status) && (
                      <button
                        onClick={() => handleExcluirEntrada(entrada)}
                        title="Excluir"
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 7h12m-9 4v6m3-6v6m3-6v6M9 7V4h6v3m-8 0h10l-1 14H8L7 7z" /></svg>
                      </button>
                    )}
                    {["em_conferencia", "conferido", "finalizado", "enderecado"].includes(entrada.status) && (
                      <button
                        onClick={() => handleImprimirVia(entrada)}
                        title="Reimprimir Via de Conferência"
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                      </button>
                    )}
                    {["recebido", "em_conferencia"].includes(entrada.status) && (
                      <button
                        onClick={() => { setRetificarModal(entrada); setRetificarVolumes(entrada.volumes ?? 1); }}
                        title="Retificar Volumes"
                        className="p-1.5 rounded hover:bg-gray-100 text-blue-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    )}

                    {/* Botões de ação por status */}
                    {entrada.status === "agendado" && (
                      <button
                        onClick={() => { setReceberEntrada(entrada); setVolumes(1); }}
                        className="px-2 py-1 rounded bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-medium"
                      >
                        Receber
                      </button>
                    )}
                    {entrada.status === "recebido" && (
                      <button
                        onClick={() => { setConferirEntrada(entrada); setSelectedConferentes([]); }}
                        className="px-2 py-1 rounded bg-purple-500 hover:bg-purple-600 text-white text-xs font-medium"
                      >
                        Conferir
                      </button>
                    )}
                    {entrada.status === "em_conferencia" && (
                      <button
                        onClick={() => setFinalizarConfEntrada(entrada)}
                        className="px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white text-xs font-medium"
                      >
                        Finalizar Conf.
                      </button>
                    )}
                    {entrada.status === "conferido" && (
                      <button
                        onClick={() => { setFinalizarEntradaModal(entrada); setSelectedOperacaoId(null); setSolicitarUpsellerFiscal(true); }}
                        className="px-2 py-1 rounded bg-teal-500 hover:bg-teal-600 text-white text-xs font-medium"
                      >
                        Finalizar Entrada
                      </button>
                    )}
                    {entrada.status === "conferido" && (
                      <button
                        onClick={() => { setFinalizarEntradaNaoFiscalModal(entrada); setSolicitarUpsellerNaoFiscal(true); }}
                        className="px-2 py-1 rounded bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium"
                      >
                        Finalizar entrada não Fiscal
                      </button>
                    )}
                    {entrada.status === "finalizado" && (
                      <button
                        onClick={() => { setEnderecaModal(entrada); setItensEnderecos({}); setItensSelecionadosEnderecamento([]); setEnderecoMassa(""); }}
                        className="px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium"
                      >
                        Endereçar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Página {page} de {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      )}

      {acaoMassa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-lg font-semibold">Ação em massa</h2>
              <button onClick={() => setAcaoMassa(null)} className="text-xl font-bold text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {(Object.keys(ACOES_MASSA) as AcaoMassa[]).map((acao) => <button key={acao} onClick={() => setAcaoMassa(acao)} className={`rounded border p-2 text-sm ${acaoMassa === acao ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200"}`}>{ACOES_MASSA[acao].label}</button>)}
              </div>
              <p className="text-sm">{entradasDaAcao.length} agendamento(s) compatível(is) serão processados.</p>
              {entradasIncompativeis > 0 && <p className="text-sm text-amber-700">{entradasIncompativeis} selecionado(s) não serão processados porque não estão com status {STATUS_LABELS[ACOES_MASSA[acaoMassa].status].label}.</p>}
              {acaoMassa === "receber" && <div className="max-h-48 space-y-2 overflow-y-auto">{entradasDaAcao.map((entrada) => <div key={entrada.id} className="flex items-center gap-3"><span className="w-36 text-sm font-mono">{entrada.numeroAgendamento}</span><Input type="number" min={1} value={volumesMassa[String(entrada.id)] ?? 1} onChange={(e) => setVolumesMassa((atual) => ({ ...atual, [String(entrada.id)]: Number(e.target.value) }))} className="w-28" /><span className="text-sm text-muted-foreground">volumes</span></div>)}</div>}
              {acaoMassa === "iniciar_conferencia" && <div className="max-h-48 space-y-2 overflow-y-auto rounded border p-3">{(usersData as any[]).map((usuario) => <label key={usuario.id} className="flex items-center gap-2"><input type="checkbox" checked={conferentesMassa.some((conferente) => conferente.id === usuario.id)} onChange={() => toggleConferenteMassa({ id: usuario.id, nome: usuario.name || usuario.nome || usuario.email })} /><span className="text-sm">{usuario.name || usuario.nome || usuario.email}</span></label>)}</div>}
              {acaoMassa === "finalizar_conferencia" && <p className="text-sm text-muted-foreground">Ao confirmar, todos os agendamentos compatíveis passam para Conferido.</p>}
              {acaoMassa === "finalizar_entrada" && <div className="space-y-3"><p className="text-sm text-red-700">Esta ação emite NF-e e lança estoque para cada agendamento concluído.</p>{Array.from(new Map(entradasDaAcao.filter((entrada) => entrada.tipo !== "cancelamento").map((entrada) => [entrada.emitenteId, entrada])).values()).map((entrada) => <div key={entrada.emitenteId}><label className="text-sm font-medium">Natureza de operação — {entrada.emitenteNome || entrada.emitenteId}</label><select className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={operacoesMassa[String(entrada.emitenteId)] ?? ""} onChange={(e) => setOperacoesMassa((atual) => ({ ...atual, [String(entrada.emitenteId)]: Number(e.target.value) || 0 }))}><option value="">Selecione...</option>{operacoesRemessaMassa.filter((operacao: any) => !operacao.emitenteId || operacao.emitenteId === entrada.emitenteId).map((operacao: any) => <option key={operacao.id} value={operacao.id}>{operacao.descricao || operacao.nome}</option>)}</select></div>)}</div>}
              {acaoMassa === "finalizar_entrada_nao_fiscal" && <p className="text-sm text-muted-foreground">Esta ação lança o estoque e finaliza os agendamentos sem emissão de NF-e.</p>}
              {(acaoMassa === "finalizar_entrada" || acaoMassa === "finalizar_entrada_nao_fiscal") && <label className="flex items-center gap-2 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm"><input type="checkbox" checked={solicitarUpsellerMassa} onChange={(event) => setSolicitarUpsellerMassa(event.target.checked)} />Dar entrada no UpSeller</label>}
            </div>
            <div className="flex justify-end gap-2 border-t p-4"><Button variant="outline" onClick={() => setAcaoMassa(null)}>Cancelar</Button><Button onClick={executarAcaoMassa} disabled={acoesMassaMutation.isPending || finalizarMassaUpsellerMutation.isPending || entradasDaAcao.length === 0} className="bg-orange-500 text-white hover:bg-orange-600">{acoesMassaMutation.isPending || finalizarMassaUpsellerMutation.isPending ? "Processando..." : "Confirmar ação"}</Button></div>
          </div>
        </div>
      )}

      {resultadoMassa && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b p-4"><h2 className="text-lg font-semibold">Resultado da ação em massa</h2><button onClick={() => setResultadoMassa(null)} className="text-xl font-bold text-gray-400 hover:text-gray-600">×</button></div>
            <div className="max-h-96 overflow-y-auto p-4 text-sm">{resultadoMassa.map((resultado) => <div key={resultado.id} className={`border-b py-2 ${resultado.resultado === "erro" ? "text-red-700" : resultado.resultado === "ignorado" ? "text-amber-700" : "text-green-700"}`}><strong>{resultado.numeroAgendamento}</strong>: {resultado.mensagem}</div>)}</div>
            <div className="flex justify-end gap-2 border-t p-4">
              {entradaIdsComCustoZero.length > 0 && (
                <Button
                  onClick={() => corrigirCustosZeradosMutation.mutate({ entradaIds: entradaIdsComCustoZero })}
                  disabled={corrigirCustosZeradosMutation.isPending}
                  className="bg-orange-500 text-white hover:bg-orange-600"
                >
                  {corrigirCustosZeradosMutation.isPending ? "Aplicando..." : "Aplicar 0,01 nos custos zerados"}
                </Button>
              )}
              <Button onClick={() => setResultadoMassa(null)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Visualizar ─────────────────────────────────────────────── */}
      {viewEntrada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Agendamento — {viewEntrada.numeroAgendamento}</h2>
              <button onClick={() => setViewEntrada(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="font-medium">Nome:</span> {viewEntrada.nome}</div>
                <div><span className="font-medium">Emitente:</span> {viewEntrada.emitenteNome ?? viewEntrada.emitenteId}</div>
                <div><span className="font-medium">Status:</span> <StatusBadge status={viewEntrada.status} /></div>
                <div><span className="font-medium">Data:</span> {formatDate(viewEntrada.dataAgendamento)}</div>
                <div><span className="font-medium">Volumes:</span> {viewEntrada.volumes ?? "-"}</div>
                <div><span className="font-medium">Nota Fiscal:</span> {viewEntrada.notaFiscal ?? "-"}</div>
              </div>
              {viewEntrada.observacoes && (
                <div className="text-sm"><span className="font-medium">Observações:</span> {viewEntrada.observacoes}</div>
              )}
              {entradaDetalhes && (entradaDetalhes as any).itens?.length > 0 && (
                <div>
                  <h3 className="font-medium text-sm mb-2">Itens</h3>
                  <table className="w-full text-xs border-collapse">
                    <thead><tr className="bg-gray-50">
                      <th className="border px-2 py-1 text-left">Cód Produto</th>
                      <th className="border px-2 py-1 text-left">Cód Entrada</th>
                      <th className="border px-2 py-1 text-left">Descrição</th>
                      <th className="border px-2 py-1 text-right">Qtd</th>
                      <th className="border px-2 py-1 text-right">Custo Unit</th>
                      <th className="border px-2 py-1 text-right">Custo Total</th>
                    </tr></thead>
                    <tbody>
                      {(entradaDetalhes as any).itens.map((item: any) => (
                        <tr key={item.id}>
                          <td className="border px-2 py-1">{item.produtoCod}</td>
                          <td className="border px-2 py-1">{item.codigoEntrada}</td>
                          <td className="border px-2 py-1">{item.produtoDescricao}</td>
                          <td className="border px-2 py-1 text-right">{parseInt(item.quantidade)}</td>
                          <td className="border px-2 py-1 text-right">{Number(item.custoUnitario).toFixed(2)}</td>
                          <td className="border px-2 py-1 text-right">{Number(item.custoTotal).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end">
              {entradaDetalhes && <Button className="mr-2" onClick={handleExportarDetalhe}>Exportar Excel</Button>}
              <Button variant="outline" onClick={() => setViewEntrada(null)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Modelo da planilha ─────────────────────────────────────── */}
      {modeloPlanilhaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Baixar planilha</h2>
              <button onClick={() => setModeloPlanilhaModal(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-muted-foreground">Selecione o modelo de Excel para os itens filtrados.</p>
              <button onClick={() => handleBaixarPlanilha("padrao")} className="w-full text-left border rounded-md p-3 hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <span className="font-medium block">Padrão</span>
                <span className="text-sm text-muted-foreground">Planilha corrida com todos os dados do agendamento e do item.</span>
              </button>
              <button onClick={() => handleBaixarPlanilha("upseller")} className="w-full text-left border rounded-md p-3 hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <span className="font-medium block">Upseller</span>
                <span className="text-sm text-muted-foreground">SKU por Cód Entrada, quantidade, estante vazio e custo unitário.</span>
              </button>
            </div>
            <div className="p-4 border-t flex justify-end">
              <Button variant="outline" onClick={() => setModeloPlanilhaModal(false)}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Textos da planilha ──────────────────────────────────────── */}
      {textoPlanilhaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Textos para copiar</h2>
              <button onClick={() => setTextoPlanilhaModal(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Nº Agendamento</label>
                <div className="flex gap-2">
                  <textarea readOnly value={`Nº Agendamento: ${textoPlanilhaModal.agendamentos}`} onFocus={(e) => e.currentTarget.select()} className="min-h-20 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" />
                  <Button variant="outline" onClick={() => navigator.clipboard.writeText(`Nº Agendamento: ${textoPlanilhaModal.agendamentos}`)}>Copiar</Button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Nome</label>
                <div className="flex gap-2">
                  <textarea readOnly value={`Nome: ${textoPlanilhaModal.nomes}`} onFocus={(e) => e.currentTarget.select()} className="min-h-20 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" />
                  <Button variant="outline" onClick={() => navigator.clipboard.writeText(`Nome: ${textoPlanilhaModal.nomes}`)}>Copiar</Button>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end">
              <Button variant="outline" onClick={() => setTextoPlanilhaModal(null)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Receber ────────────────────────────────────────────────── */}
      {receberEntrada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Receber — {receberEntrada.numeroAgendamento}</h2>
              <button onClick={() => setReceberEntrada(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">Confirme a quantidade de volumes recebidos para este agendamento.</p>
              <div>
                <label className="text-sm font-medium">Quantidade de Volumes *</label>
                <Input
                  type="number"
                  min={1}
                  value={volumes}
                  onChange={e => setVolumes(Number(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReceberEntrada(null)}>Cancelar</Button>
              <Button onClick={handleReceber} disabled={receberMutation.isPending} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                {receberMutation.isPending ? "Salvando..." : "Confirmar Recebimento"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Conferir ───────────────────────────────────────────────── */}
      {conferirEntrada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Iniciar Conferência — {conferirEntrada.numeroAgendamento}</h2>
              <button onClick={() => setConferirEntrada(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">Selecione os conferentes responsáveis por esta conferência.</p>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-2">
                {(usersData as any[]).map((u: any) => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={!!selectedConferentes.find(c => c.id === u.id)}
                      onChange={() => toggleConferente({ id: u.id, nome: u.name || u.nome || u.email })}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{u.name || u.nome || u.email}</span>
                  </label>
                ))}
              </div>
              {selectedConferentes.length > 0 && (
                <p className="text-xs text-muted-foreground">Selecionados: {selectedConferentes.map(c => c.nome).join(", ")}</p>
              )}
              <div className="border-t pt-3">
                <p className="text-xs text-muted-foreground mb-2">Ao confirmar, será gerada a via de conferência para impressão.</p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isLoadingConferir || !entradaConferir}
                  onClick={() => {
                    const itens = (entradaConferir as any)?.itens;
                    if (!itens || itens.length === 0) {
                      toast.error("Aguarde o carregamento dos itens antes de imprimir.");
                      return;
                    }
                    handleImprimirVia({ ...conferirEntrada!, itens });
                  }}
                >
                  {isLoadingConferir ? "Carregando..." : "Imprimir Via de Conferência"}
                </Button>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConferirEntrada(null)}>Cancelar</Button>
              <Button onClick={handleIniciarConferencia} disabled={iniciarConfMutation.isPending} className="bg-purple-500 hover:bg-purple-600 text-white">
                {iniciarConfMutation.isPending ? "Salvando..." : "Iniciar Conferência"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Finalizar Conferência ──────────────────────────────────── */}
      {finalizarConfEntrada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Finalizar Conferência</h2>
              <button onClick={() => setFinalizarConfEntrada(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4">
              <p className="text-sm">Confirmar finalização da conferência do agendamento <strong>{finalizarConfEntrada.numeroAgendamento}</strong>?</p>
              <p className="text-xs text-muted-foreground mt-2">O status será alterado para <strong>Conferido</strong>.</p>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFinalizarConfEntrada(null)}>Cancelar</Button>
              <Button onClick={handleFinalizarConferencia} disabled={finalizarConfMutation.isPending} className="bg-green-500 hover:bg-green-600 text-white">
                {finalizarConfMutation.isPending ? "Salvando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Finalizar Entrada ──────────────────────────────────────── */}
      {finalizarEntradaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Finalizar Entrada — {finalizarEntradaModal.numeroAgendamento}</h2>
              <button onClick={() => setFinalizarEntradaModal(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-4">
              {finalizarEntradaModal.tipo === 'cancelamento' ? (
                <p className="text-sm text-muted-foreground">Esta entrada é de cancelamento. Os itens serão lançados no estoque sem emissão de NF-e.</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">Selecione a natureza de operação para emissão da nota fiscal e lançamento dos itens no estoque.</p>
                  <div>
                    <label className="text-sm font-medium">Natureza de Operação *</label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                      value={selectedOperacaoId ?? ""}
                      onChange={e => setSelectedOperacaoId(Number(e.target.value) || null)}
                    >
                      <option value="">Selecione...</option>
                      {operacoesRemessa.map((o: any) => (
                        <option key={o.id} value={o.id}>{o.descricao || o.nome}</option>
                      ))}
                    </select>
                    {operacoesRemessa.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">Nenhuma natureza de operação do tipo "Remessa para Armazém" encontrada para este emitente.</p>
                    )}
                  </div>
                </>
              )}
              <label className="flex items-center gap-2 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm"><input type="checkbox" checked={solicitarUpsellerFiscal} onChange={(event) => setSolicitarUpsellerFiscal(event.target.checked)} />Dar entrada no UpSeller</label>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFinalizarEntradaModal(null)}>Cancelar</Button>
              <Button onClick={handleFinalizarEntrada} disabled={finalizarEntradaMutation.isPending || (finalizarEntradaModal.tipo !== 'cancelamento' && !selectedOperacaoId)} className="bg-teal-500 hover:bg-teal-600 text-white">
                {finalizarEntradaMutation.isPending ? "Processando..." : "Finalizar Entrada"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Finalizar Entrada não Fiscal ────────────────────────────── */}
      {finalizarEntradaNaoFiscalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Finalizar entrada não Fiscal — {finalizarEntradaNaoFiscalModal.numeroAgendamento}</h2>
              <button onClick={() => setFinalizarEntradaNaoFiscalModal(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4">
              <p className="text-sm">Confirmar a finalização sem emissão de nota fiscal?</p>
              <p className="text-xs text-muted-foreground mt-2">Os itens serão lançados no estoque e o status será alterado para <strong>Finalizado</strong>.</p>
              <label className="mt-4 flex items-center gap-2 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm"><input type="checkbox" checked={solicitarUpsellerNaoFiscal} onChange={(event) => setSolicitarUpsellerNaoFiscal(event.target.checked)} />Dar entrada no UpSeller</label>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFinalizarEntradaNaoFiscalModal(null)}>Cancelar</Button>
              <Button onClick={handleFinalizarEntradaNaoFiscal} disabled={finalizarEntradaNaoFiscalMutation.isPending} className="bg-gray-600 hover:bg-gray-700 text-white">
                {finalizarEntradaNaoFiscalMutation.isPending ? "Processando..." : "Finalizar entrada não Fiscal"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Endereçar ──────────────────────────────────────────────── */}
      {enderecaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Endereçar Itens — {enderecaModal.numeroAgendamento}</h2>
              <button onClick={() => setEnderecaModal(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">Para cada item, informe ao menos um endereço de destino. O primeiro endereço é obrigatório.</p>
              {entradaEnderecar && (
                <div className="rounded-md border bg-gray-50 p-3 space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input type="checkbox" checked={todosItensEnderecamentoSelecionados} onChange={toggleTodosItensEnderecamento} />
                    Selecionar todos ({itensDoEnderecamento.length})
                  </label>
                  <div className="flex gap-2">
                    <select className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm" value={enderecoMassa} onChange={(e) => setEnderecoMassa(e.target.value)}>
                      <option value="">Selecionar endereço para os itens marcados...</option>
                      {(enderecosData as any[]).map((endereco: any) => {
                        const codigo = endereco.codigo || endereco.endereco;
                        return <option key={endereco.id} value={codigo}>{codigo}</option>;
                      })}
                    </select>
                    <Button type="button" variant="outline" onClick={aplicarEnderecoMassa}>Aplicar aos selecionados</Button>
                  </div>
                </div>
              )}
              {entradaEnderecar && (entradaEnderecar as any).itens?.map((item: any) => (
                <div key={item.id} className="border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" aria-label={`Selecionar ${item.codigoEntrada || item.produtoCod}`} checked={itensSelecionadosEnderecamento.includes(item.id)} onChange={() => toggleItemEnderecamento(item.id)} />
                      <div>
                        <span className="font-medium text-sm">{item.codigoEntrada || item.produtoCod}</span>
                        <span className="text-xs text-muted-foreground ml-2">{item.produtoDescricao}</span>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">Qtd: {parseInt(item.quantidade)}</span>
                  </div>
                  {(itensEnderecos[item.id] ?? [{ endereco: "", quantidade: 0 }]).map((endObj, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <button
                        type="button"
                        className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm text-left hover:bg-muted/50 transition-colors"
                        onClick={() => { setEnderecoSearch(""); setEnderecoSelectModal({ itemId: item.id, idx }); }}
                      >
                        {endObj.endereco ? endObj.endereco : <span className="text-muted-foreground">{idx === 0 ? "Selecionar endereço (obrigatório)..." : "Selecionar endereço (opcional)..."}</span>}
                      </button>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Qtd"
                        className="h-9 w-24 rounded-md border border-input bg-background px-2 py-1 text-sm text-center"
                        value={endObj.quantidade || ""}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 0;
                          const updated = [...(itensEnderecos[item.id] ?? [{ endereco: "", quantidade: 0 }])];
                          updated[idx] = { ...updated[idx], quantidade: val };
                          setItensEnderecos(prev => ({ ...prev, [item.id]: updated }));
                        }}
                      />
                      {idx === (itensEnderecos[item.id] ?? [{ endereco: "", quantidade: 0 }]).length - 1 && (
                        <button
                          onClick={() => setItensEnderecos(prev => ({ ...prev, [item.id]: [...(prev[item.id] ?? [{ endereco: "", quantidade: 0 }]), { endereco: "", quantidade: 0 }] }))}
                          className="text-orange-500 hover:text-orange-700 text-lg font-bold"
                          title="Adicionar endereço"
                        >+</button>
                      )}
                      {idx > 0 && (
                        <button
                          onClick={() => {
                            const updated = (itensEnderecos[item.id] ?? [{ endereco: "", quantidade: 0 }]).filter((_, i) => i !== idx);
                            setItensEnderecos(prev => ({ ...prev, [item.id]: updated }));
                          }}
                          className="text-red-400 hover:text-red-600 text-lg font-bold"
                          title="Remover endereço"
                        >×</button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEnderecaModal(null)}>Cancelar</Button>
              <Button onClick={handleEnderecar} disabled={enderecaMutation.isPending} className="bg-orange-500 hover:bg-orange-600 text-white">
                {enderecaMutation.isPending ? "Salvando..." : "Confirmar Endereçamento"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Retificar Volumes ──────────────────────────────────────── */}
      {retificarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Retificar Volumes — {retificarModal.numeroAgendamento}</h2>
              <button onClick={() => setRetificarModal(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">Corrija a quantidade de volumes para este agendamento.</p>
              <div>
                <label className="text-sm font-medium">Quantidade de Volumes *</label>
                <Input
                  type="number"
                  min={1}
                  value={retificarVolumes}
                  onChange={e => setRetificarVolumes(Number(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRetificarModal(null)}>Cancelar</Button>
              <Button onClick={handleRetificar} disabled={retificarMutation.isPending} className="bg-blue-500 hover:bg-blue-600 text-white">
                {retificarMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Selecionar Endereço ─────────────────────────────────────────────────── */}
      {enderecoSelectModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Selecionar Endereço</h2>
              <button onClick={() => setEnderecoSelectModal(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-3 border-b">
              <Input
                placeholder="Buscar endereço..."
                value={enderecoSearch}
                onChange={e => setEnderecoSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {(enderecosData as any[])
                .filter((a: any) => {
                  const cod = (a.codigo || a.endereco || "").toLowerCase();
                  return !enderecoSearch || cod.includes(enderecoSearch.toLowerCase());
                })
                .map((a: any) => {
                  const cod = a.codigo || a.endereco;
                  const { itemId, idx } = enderecoSelectModal;
                  const isSelected = (itensEnderecos[itemId] ?? [{ endereco: "", quantidade: 0 }])[idx]?.endereco === cod;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-muted/50 transition-colors ${
                        isSelected ? "bg-teal-50 font-medium text-teal-700" : ""
                      }`}
                      onClick={() => {
                        const { itemId, idx } = enderecoSelectModal;
                        const updated = [...(itensEnderecos[itemId] ?? [{ endereco: "", quantidade: 0 }])];
                        updated[idx] = { ...updated[idx], endereco: cod };
                        setItensEnderecos(prev => ({ ...prev, [itemId]: updated }));
                        setEnderecoSelectModal(null);
                      }}
                    >
                      {cod}
                    </button>
                  );
                })
              }
              {(enderecosData as any[]).filter((a: any) => {
                const cod = (a.codigo || a.endereco || "").toLowerCase();
                return !enderecoSearch || cod.includes(enderecoSearch.toLowerCase());
              }).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhum endereço encontrado.</p>
              )}
            </div>
            <div className="p-3 border-t flex justify-end flex-shrink-0">
              <Button variant="outline" onClick={() => setEnderecoSelectModal(null)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
